import { ArrowLeft, PieChart, TrendingUp } from "lucide-react";
import React, { useMemo } from "react";
import { PerformanceMetric } from "../../../services/performanceMetricsService";
import type { ProductTagsData } from "../../../types/productTags";
import { shouldHidePerformanceChart } from "../../../utils/portfolioUtils";
import PerformanceUnavailableMessage from "../../PerformanceUnavailableMessage";
import PortfolioComposition from "./PortfolioComposition";
import PortfolioHeroSection from "./PortfolioHeroSection";
import PortfolioPerformanceChart from "./PortfolioPerformanceChart";
import { DisplayPortfolioData } from "./types";
import { usePortfolioPerformance } from "./usePortfolioPerformance";

export interface PortfolioDisplayProps {
  portfolio: DisplayPortfolioData;
  performanceMetrics?: PerformanceMetric[];
  loading?: boolean;
  error?: string | null;
  isDarkMode?: boolean;
  forceLightMode?: boolean;
  showBackButton?: boolean;
  backButtonText?: string;
  onBackClick?: () => void;
  showWatermark?: boolean;
  showProviderInfo?: boolean;
  hideComposition?: boolean;
  /** Product tags to display in hero section */
  tags?: ProductTagsData | null;
  /** Callback when tags are clicked */
  onTagsClick?: () => void;
  renderHeader?: () => React.ReactNode;
  renderAfterChart?: () => React.ReactNode;
  renderAfterComposition?: () => React.ReactNode;
  renderFooter?: () => React.ReactNode;
}

const PortfolioDisplay: React.FC<PortfolioDisplayProps> = ({
  portfolio,
  performanceMetrics,
  loading = false,
  error = null,
  isDarkMode: propIsDarkMode = false,
  forceLightMode = false,
  showBackButton = false,
  backButtonText = "Back",
  onBackClick,
  showWatermark = true,
  showProviderInfo = true,
  hideComposition = false,
  tags,
  onTagsClick,
  renderHeader,
  renderAfterChart,
  renderAfterComposition,
  renderFooter,
}) => {
  const isDarkMode = forceLightMode ? false : propIsDarkMode;

  // Merge performance metrics from props or portfolio
  const metrics = useMemo(
    () => performanceMetrics || portfolio.performanceMetrics || [],
    [performanceMetrics, portfolio.performanceMetrics]
  );

  const { oneYearPerformance } = usePortfolioPerformance({
    performanceMetrics: metrics,
    createdAt: portfolio.createdAt,
  });

  // Get latest metric date
  const latestMetricDate = useMemo(() => {
    const factorMetrics = metrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return null;
    const sorted = [...factorMetrics].sort(
      (a, b) => new Date(b.metricDate).getTime() - new Date(a.metricDate).getTime()
    );
    return sorted[0]?.metricDate || null;
  }, [metrics]);

  const hasPerformanceData = metrics.length > 1;
  const isPositive = oneYearPerformance !== null ? oneYearPerformance >= 0 : true;

  // Loading state
  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
              isDarkMode ? "border-teal-400" : "border-teal-600"
            } mx-auto mb-4`}
          ></div>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>Loading portfolio details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          {showBackButton && onBackClick && (
            <button onClick={onBackClick} className="text-teal-600 hover:text-teal-700 underline">
              {backButtonText}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state
  const isEmpty =
    portfolio.portfolioStocks.length === 0 &&
    portfolio.portfolioBaskets.length === 0 &&
    portfolio.portfolioOptions.length === 0;

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      {/* Back Button Header */}
      {showBackButton && onBackClick && (
        <div
          className={`${isDarkMode ? "bg-gray-800" : "bg-white"} border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          } p-6`}
        >
          <div className="max-w-6xl mx-auto">
            <button
              onClick={onBackClick}
              className={`flex items-center ${
                isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"
              } transition-colors mb-4`}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {backButtonText}
            </button>
          </div>
        </div>
      )}

      {/* Custom Header */}
      {renderHeader && renderHeader()}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Hero Section */}
        <PortfolioHeroSection
          portfolio={portfolio}
          oneYearPerformance={shouldHidePerformanceChart(portfolio.id) ? null : oneYearPerformance}
          latestMetricDate={shouldHidePerformanceChart(portfolio.id) ? null : latestMetricDate}
          isDarkMode={isDarkMode}
          showProviderInfo={showProviderInfo}
          tags={tags}
          onTagsClick={onTagsClick}
        />

        {/* Performance Chart */}
        {shouldHidePerformanceChart(portfolio.id) ? (
          <div
            className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-2xl p-6 shadow-xl border ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <PerformanceUnavailableMessage isDarkMode={isDarkMode} variant="card" />
          </div>
        ) : hasPerformanceData ? (
          <div
            className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-2xl p-6 shadow-xl border ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="mb-4">
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                } mb-1 flex items-center gap-2`}
              >
                <TrendingUp className={`w-5 h-5 ${isPositive ? "text-teal-600" : "text-red-600"}`} />
                Performance History
              </h2>
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                Track performance over time
              </p>
            </div>

            <PortfolioPerformanceChart
              performanceMetrics={metrics}
              createdAt={portfolio.createdAt}
              isDarkMode={isDarkMode}
              showWatermark={showWatermark}
            />
          </div>
        ) : null}

        {/* Custom content after chart */}
        {renderAfterChart && renderAfterChart()}

        {/* Portfolio Composition */}
        {!hideComposition &&
          (!isEmpty ? (
            <PortfolioComposition portfolio={portfolio} isDarkMode={isDarkMode} />
          ) : (
            <div
              className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-2xl shadow-xl border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              } overflow-hidden`}
            >
              <div className="text-center py-12">
                <div
                  className={`w-16 h-16 rounded-full ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  } flex items-center justify-center mx-auto mb-4`}
                >
                  <PieChart className={`w-8 h-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                </div>
                <p className={`${isDarkMode ? "text-gray-400" : "text-gray-600"} font-medium`}>
                  No holdings in this portfolio
                </p>
                <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} mt-1`}>
                  This portfolio is currently empty
                </p>
              </div>
            </div>
          ))}

        {/* Custom content after composition */}
        {renderAfterComposition && renderAfterComposition()}
      </div>

      {/* Custom Footer */}
      {renderFooter && renderFooter()}
    </div>
  );
};

export default PortfolioDisplay;
